import React, {useCallback, useState} from 'react'
import {Button, NavButtonProps} from 'Event/Dashboard/components/NavButton'
import {useAttendeeVariables} from 'Event'
import {useEvent} from 'Event/EventProvider'
import {useEditMode} from 'Event/EditModeProvider'
import {Certificate} from 'lib/event-api/certificates/types'
import {api} from 'lib/url'
import {FileLocation, downloadFile} from 'lib/http-client'
import {useSnackbar} from 'notistack'
import {useInterval} from 'lib/interval'

const readyPollIntervalMs = 3000

export default function CertificateButton(props: NavButtonProps) {
  const {certificateId} = props
  const {event} = useEvent()
  const isEditMode = useEditMode()

  const certificate = event.certificates.find((certificate) => {
    if (!certificateId) {
      return false
    }

    return certificate.id === parseInt(certificateId)
  })
  if (!certificate) {
    return <Button {...props} />
  }

  if (isEditMode) {
    return <Button {...props} />
  }

  return <DownloadButton certificate={certificate} button={props} />
}

function DownloadButton(props: {
  certificate: Certificate
  button: NavButtonProps
}) {
  const {client} = useEvent()
  const {certificate, button} = props
  const {id: certificateId, title: certificateTitle} = certificate
  const [processing, setProcessing] = useState(false)
  const [sent, setSent] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloaded, setDownloaded] = useState(false)

  const {enqueueSnackbar} = useSnackbar()

  const blocks = certificate.template?.blocks || {}

  const v = useAttendeeVariables()
  const ids = Object.keys(blocks)

  const download = useCallback(async () => {
    if (!sent) {
      return
    }

    if (downloading) {
      return
    }

    setDownloading(true)

    const {url} = await client.get<{url: string | null}>(
      api(`/certificates/${certificateId}/download`),
    )

    if (!url) {
      return
    }

    downloadFile(url, `${certificateTitle}.pdf`)
    setDownloading(false)
    setDownloaded(true)
  }, [sent, client, certificateId, certificateTitle, downloading])

  useInterval(download, readyPollIntervalMs, downloaded)

  const generate = () => {
    if (sent) {
      return
    }

    if (processing) {
      return
    }

    setProcessing(true)

    ids.forEach((id) => {
      blocks[id] = {
        ...blocks[id],
        text: v(blocks[id].text),
      }
    })
    const url = api(`/certificates/${certificate.id}/download`)
    client
      .post<FileLocation[]>(url, {
        template: {
          blocks,
        },
      })
      .then(() => {
        setSent(true)
        enqueueSnackbar('Your certificate will be sent to your email', {
          variant: 'success',
        })
      })
      .finally(() => {
        setProcessing(false)
      })
  }

  const busy = processing || (sent && !downloaded)

  const firstRowText = busy ? 'Generating...' : button.text
  const secondRowText = busy ? '' : button.textSecond

  const backgroundColor = busy
    ? button.hoverBackgroundColor
    : button.backgroundColor
  const textColor = busy ? button.hoverTextColor : button.textColor

  return (
    <Button
      {...button}
      text={firstRowText}
      textSecond={secondRowText}
      onClick={generate}
      disabled={processing || downloading}
      backgroundColor={backgroundColor ?? button.backgroundColor}
      textColor={textColor ?? button.textColor}
    />
  )
}
